import { CheckRounded, CloseRounded } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccountsStore } from '../../store/AccountsStore'
import { PATHS } from '../PATHS'
import { blueSky, colors } from '../theme'

interface GettingStartedPanelProps {
  inModal?: boolean
}

interface StepCardProps {
  title: string
  subtitle: string
  bulletPoints: ReactNode[]
  link: string
  buttonText: string
}

const StepCard: FC<StepCardProps> = ({ title, subtitle, bulletPoints, link, buttonText }) => {
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.6,
        flex: '1 1 300px',
        minWidth: { xs: '100%', md: 300 },
        maxWidth: { xs: '100%', md: '32%' },
        width: '100%',
        mb: { xs: 3, md: 0 },
        p: { xs: 2.5, md: 3 },
        borderRadius: 5,
        border: '1px solid #E3E8F3',
        background:
          'linear-gradient(180deg, rgba(250,252,255,0.96) 0%, rgba(255,255,255,0.98) 60%, rgba(248,250,255,0.98) 100%)',
        boxShadow: '0px 18px 40px rgba(37, 58, 110, 0.05)',
      }}
    >
      <Typography
        sx={{
          color: blueSky,
          fontSize: { xs: 15, md: 16 },
          lineHeight: 1.05,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: colors.cello,
          fontSize: { xs: 16, md: 17 },
          lineHeight: 1.35,
          fontWeight: 700,
          maxWidth: 300,
        }}
      >
        {subtitle}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15, mt: 0.2 }}>
        {bulletPoints.map((bulletPoint, index) => (
          <Box key={`${title}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckRounded sx={{ color: '#58A56A', fontSize: 20, mt: '2px' }} />
            <Typography
              sx={{
                color: '#59647F',
                fontSize: 16,
                lineHeight: 1.35,
                fontWeight: 500,
              }}
            >
              {bulletPoint}
            </Typography>
          </Box>
        ))}
      </Box>
      {accountsStore.connectedAccount && (
        <Button
          variant="outlined"
          onClick={() => navigate(link)}
          sx={{
            mt: 'auto',
            alignSelf: 'center',
            minWidth: 272,
            height: 54,
            px: 3.4,
            borderRadius: '999px',
            borderColor: '#BED0E8',
            color: blueSky,
            textTransform: 'uppercase',
            fontSize: '1rem',
            fontWeight: 800,
            letterSpacing: '0.01em',
            backgroundColor: 'rgba(255,255,255,0.88)',
            '&:hover': {
              borderColor: '#A8BCD9',
              backgroundColor: 'white',
            },
          }}
        >
          {buttonText}
        </Button>
      )}
    </Box>
  )
}

export const GettingStartedPanel: FC<GettingStartedPanelProps> = ({ inModal = false }) => {
  const accountsStore = useAccountsStore()
  const association = accountsStore.connectedCustomer
  const associationPublicPageLink = association?.id
    ? `https://petso.fr/associations/${association.id}`
    : 'https://petso.fr'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        px: { xs: 3, md: inModal ? 0 : 8 },
        pt: { xs: 2, md: inModal ? 0 : 2.5 },
        pb: { xs: 3, md: inModal ? 0 : 8 },
        gap: { xs: 2.5, md: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.2,
          maxWidth: 900,
          px: { xs: 0, md: inModal ? 0 : 1 },
        }}
      >
        <Typography
          sx={{
            color: blueSky,
            fontSize: { xs: 15, md: 16 },
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Bienvenue sur Petso
        </Typography>
        <Typography
          variant="h2"
          sx={{
            color: colors.cello,
            fontSize: { xs: 22, md: 29 },
            lineHeight: { xs: 1.14, md: 1.08 },
            fontWeight: 800,
            maxWidth: 620,
          }}
        >
          Gérez vos animaux, vos adoptants et vos équipes dans un seul espace clair.
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: { xs: 16, md: 16.8 },
            lineHeight: 1.38,
            color: '#59647F',
            maxWidth: { xs: 760, md: 'none' },
            whiteSpace: { xs: 'normal', md: 'nowrap' },
          }}
        >
          Petso simplifie le suivi de vos animaux, la gestion de vos annonces et vous aide à faire avancer chaque
          adoption plus facilement.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: { xs: 1.5, md: 2.4 },
          justifyContent: 'left',
          alignItems: 'stretch',
          mt: { xs: 1.5, md: 2.25 },
          mb: { xs: 4, md: 0 },
        }}
      >
        <StepCard
          title="Animaux"
          subtitle="Centralisez le suivi de chaque animal au même endroit."
          bulletPoints={[
            'Prise en charge',
            <>
              Publication sur{' '}
              <Box
                component="a"
                href={associationPublicPageLink}
                target="_blank"
                rel="noreferrer"
                sx={{
                  color: '#698DAC',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  textUnderlineOffset: '0.15em',
                }}
              >
                votre page vitrine
              </Box>{' '}
              et mise à jour automatique
            </>,
            'Suivi de santé et rappels à venir',
          ]}
          link={PATHS.ajouterAnimal.absolute}
          buttonText="Ajouter un animal"
        />
        <StepCard
          title="Adoptants"
          subtitle="Pilotez chaque procédure d’adoption sans vous disperser."
          bulletPoints={[
            'Ajoutez vos adoptants et associez une adoption à un animal',
            "Suivez la réception des frais d'adoption via paiement en ligne",
            "Générez et envoyez le certificat et contrat d'adoption pour signature",
          ]}
          link={PATHS.ajouterAdoptant.absolute}
          buttonText="Ajouter un adoptant"
        />
        <StepCard
          title="Membres"
          subtitle="Organisez l’équipe autour d'un espace collaboratif."
          bulletPoints={[
            'Ajoutez vos membres',
            'Définissez un niveau de permission pour chacun',
            'Rattachez-les aux animaux dont ils sont en charge',
          ]}
          link={PATHS.ajouterBenevole.absolute}
          buttonText="Ajouter un membre"
        />
      </Box>
    </Box>
  )
}

interface GettingStartedModalProps {
  open: boolean
  onClose: () => void
}

export const GettingStartedModal: FC<GettingStartedModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 5,
          border: '1px solid #E4E8F2',
          boxShadow: '0 24px 60px rgba(20, 32, 60, 0.16)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ px: { xs: 1, md: 3 }, py: { xs: 2, md: 3 }, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            width: 38,
            height: 38,
            borderRadius: '999px',
            color: '#7A8197',
            backgroundColor: '#F7F9FD',
            border: '1px solid #E4E8F2',
            '&:hover': { backgroundColor: '#EEF2F8' },
          }}
        >
          <CloseRounded />
        </IconButton>
        <GettingStartedPanel inModal />
      </DialogContent>
    </Dialog>
  )
}
